// @flow
import { MODULARUI_STATUS } from "../../constants/Constants";
import { ApplicationModel } from "../../models";

import type { Reducer } from "redux";
import type { ReduxAction } from "../types";
import type { ModularUIState } from "./types";
import type { ModularUIModel } from "../../models";

/**
 */
const updateStatus = (
  state: ModularUIState,
  {
    key,
    status,
    requestOptions,
  }: {
    key: string,
    status: $Keys<typeof MODULARUI_STATUS>,
    requestOptions: any,
  },
) => {
  // model should always be available when status is not loading
  if (status !== MODULARUI_STATUS.LOADING && !state[key]) {
    return state;
  }

  return {
    ...state,
    [key]: {
      ...state[key],
      status,
      lastModification: Date.now(),
      requestOptions: requestOptions || state[key]?.requestOptions,
    },
  };
};

/**
 */
const setModel = (
  state: ModularUIState,
  { key, model }: { key: string, model: ?ModularUIModel },
) => {
  if (!state[key]) {
    return state;
  }

  if (model) {
    return {
      ...state,
      [key]: {
        ...state[key],
        model,
        lastModification: Date.now(),
        status: MODULARUI_STATUS.FINISHED,
      },
    };
  }

  // eslint-disable-next-line no-console
  console.debug("No model for setModel");
  return state;
};

/**
 */
const initModularUI = (
  state: ModularUIState,
  models: Array<{ key: string, model: ModularUIModel }>,
) => {
  const newState = { ...state };

  models.forEach(({ key, model }) => {
    newState[key] = {
      status: MODULARUI_STATUS.FINISHED,
      lastModification: Date.now(),
      model,
    };
  });

  return newState;
};

/**
 */
const getModelKey = (state: ModularUIState, model: ModularUIModel) =>
  Object.keys(state).find((key) => {
    const connectKey = state[key]?.model?.connectKey ?? "";
    return connectKey === model.connectKey;
  });

/**
 */
const updateModel = (state: ModularUIState, model: ModularUIModel) => {
  const modelKey = getModelKey(state, model);

  if (modelKey) {
    return setModel(state, { key: modelKey, model });
  }

  // eslint-disable-next-line no-console
  console.debug(
    `ModularUIReducer: Cannot update model with key ${model.connectKey}`,
  );
  return state;
};

/**
 */
const removeKey = (modelKey: string, state: ModularUIState): ModularUIState => {
  const newState = { ...state };
  delete newState[modelKey];
  return newState;
};

/**
 * Remove all but application models
 */
const resetModularUI = (state: ModularUIState) => {
  const newState: ModularUIState = {};

  for (const key in state) {
    if (state[key].model instanceof ApplicationModel) {
      newState[key] = { ...state[key] };
    }
  }

  return newState;
};

const initialState: ModularUIState = {};

/**
 * Modular UI Reducer
 */
export const ModularUIReducer: Reducer<ModularUIState, ReduxAction> = (
  state = initialState,
  action,
) => {
  if (!action) {
    return state;
  }

  switch (action.type) {
    case "MODULARUI/INIT":
      return initModularUI(state, action.payload);

    case "MODULARUI/RESET":
      return resetModularUI(state);

    case "MODULARUI/STATUS":
      return updateStatus(state, action.payload);

    case "MODULARUI/SET":
      return setModel(state, action.payload);

    case "MODULARUI/UPDATE":
    case "MODULARUI/UPDATE_FORM":
      return updateModel(state, action.payload);

    case "MODULARUI/REMOVE_KEY":
      return removeKey(action.payload, state);

    default:
      return state;
  }
};
